import { useEffect, useState } from 'react'
import { Cookie, Settings2, X } from 'lucide-react'
import { webUrl } from '@/constants/site'
import {
  ACCEPT_ALL, REJECT_ALL,
  type ConsentState,
  hasConsentDecision, getStoredConsent, saveConsent,
  onOpenCookiePreferences,
} from '@/lib/consent'

/**
 * Banner cookie GDPR (linee guida Garante): "Accetta tutti" e "Rifiuta tutti"
 * con pari evidenza sul primo livello, scelta granulare via "Personalizza",
 * nessuna categoria non-necessaria pre-attivata, riapribile dal footer.
 *
 * Integrato con Google Consent Mode v2 (vedi `@/lib/consent`). Il default
 * `denied` è impostato in `index.html` prima di GTM.
 */
export function CookieConsentBanner() {
  const [open, setOpen] = useState(false)
  const [customizing, setCustomizing] = useState(false)
  const [prefs, setPrefs] = useState<ConsentState>(REJECT_ALL)

  // Mostra al primo accesso (nessuna scelta salvata) + riapertura dal footer.
  useEffect(() => {
    if (!hasConsentDecision()) setOpen(true)
    return onOpenCookiePreferences(() => {
      const saved = getStoredConsent()
      setPrefs(saved?.categories ?? REJECT_ALL)
      setCustomizing(true)
      setOpen(true)
    })
  }, [])

  if (!open) return null

  function decide(state: ConsentState) {
    saveConsent(state)
    setOpen(false)
    setCustomizing(false)
  }

  return (
    <div
      // z-[70]: sotto i sheet di prenotazione (z-[80], modali Radix che rendono
      // inerte il resto della pagina): altrimenti il banner li coprirebbe senza
      // poter essere cliccato. Su mobile è una barra compatta.
      className="fixed inset-x-0 bottom-0 z-[70] p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] sm:p-4"
      role="dialog"
      aria-modal="false"
      aria-label="Preferenze cookie"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl sm:p-6">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-holly-50 text-holly-600 sm:flex">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold text-gray-900 sm:text-base">Rispettiamo la tua privacy</h2>
            <p className="mt-1 text-xs leading-relaxed text-gray-600 sm:text-sm">
              Usiamo cookie tecnici necessari e, previo tuo consenso, cookie analitici e di
              marketing. Dettagli nella{' '}
              <a href={webUrl('/cookie-policy')} className="font-medium text-holly-700 underline hover:text-holly-800">
                Cookie Policy
              </a>{' '}
              e nella{' '}
              <a href={webUrl('/privacy')} className="font-medium text-holly-700 underline hover:text-holly-800">
                Privacy Policy
              </a>.
            </p>

            {/* Pannello granulare */}
            {customizing && (
              <div className="mt-4 space-y-2.5 rounded-xl border border-gray-100 bg-gray-50 p-3">
                <CategoryRow
                  title="Necessari"
                  description="Indispensabili per il funzionamento del sito (sicurezza, login, preferenze). Sempre attivi."
                  checked
                  disabled
                />
                <CategoryRow
                  title="Analitici"
                  description="Statistiche aggregate sull'uso del sito per migliorarlo."
                  checked={prefs.analytics}
                  onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                />
                <CategoryRow
                  title="Marketing"
                  description="Misurazione delle campagne e contenuti personalizzati."
                  checked={prefs.marketing}
                  onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                />
              </div>
            )}

            {/* Azioni */}
            <div className="mt-3 grid grid-cols-2 gap-2 sm:mt-4 sm:flex sm:flex-row sm:flex-wrap sm:items-center">
              {customizing ? (
                <>
                  <button
                    onClick={() => decide(prefs)}
                    className="order-1 col-span-2 rounded-lg bg-holly-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-holly-700"
                  >
                    Salva preferenze
                  </button>
                  <button
                    onClick={() => decide(ACCEPT_ALL)}
                    className="order-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Accetta tutti
                  </button>
                  <button
                    onClick={() => decide(REJECT_ALL)}
                    className="order-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Rifiuta tutti
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => decide(ACCEPT_ALL)}
                    className="order-1 rounded-lg bg-holly-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-holly-700"
                  >
                    Accetta tutti
                  </button>
                  <button
                    onClick={() => decide(REJECT_ALL)}
                    className="order-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Rifiuta tutti
                  </button>
                  <button
                    onClick={() => { setPrefs(getStoredConsent()?.categories ?? REJECT_ALL); setCustomizing(true) }}
                    className="order-3 col-span-2 inline-flex items-center justify-center gap-1.5 rounded-lg px-4 py-1.5 text-xs font-medium text-holly-700 hover:bg-holly-50 sm:ml-auto sm:py-2.5 sm:text-sm"
                  >
                    <Settings2 className="h-4 w-4" /> Personalizza
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Chiudi = rifiuta (equivale a "Rifiuta tutti", come da linee guida) */}
          <button
            onClick={() => decide(REJECT_ALL)}
            aria-label="Chiudi e rifiuta i cookie non necessari"
            className="ml-1 shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

function CategoryRow({
  title, description, checked, disabled, onChange,
}: {
  title:        string
  description:  string
  checked:      boolean
  disabled?:    boolean
  onChange?:    (value: boolean) => void
}) {
  return (
    <label className={`flex items-start justify-between gap-3 ${disabled ? 'opacity-70' : 'cursor-pointer'}`}>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-gray-900">{title}</span>
        <span className="block text-xs text-gray-500">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-holly-600 focus:ring-holly-500 disabled:cursor-not-allowed"
      />
    </label>
  )
}
